import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  MenuItem,
  Grid,
  IconButton,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import Q8Button from "../components/Q8Button";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";

const ChangeHistoryDialog = ({
  open,
  onClose,
  history = [],
  filters = {
    status: "all",
    dateFrom: "",
    dateTo: "",
    code: "",
  },
  onFiltersChange,
}) => {
  const historyTableRef = useRef(null);
  const tableInstance = useRef(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [filteredData, setFilteredData] = useState([]);

  // Funzione di formattazione date custom
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleString("it-IT", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch (error) {
      console.error("Error formatting date:", error, dateString);
      return dateString || "";
    }
  };

  // Funzione per espandere/collassare una riga
  const toggleRow = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const applyFilters = (data) => {
    if (!Array.isArray(data)) return [];

    // Aggiungi campi formattati per le date
    const processedData = data.map((item) => {
      // Parse JSON strings if needed
      let originalRecord = item.originalRecordJson;
      let modifiedRecord = item.modifiedRecordJson;

      if (typeof originalRecord === "string") {
        try {
          originalRecord = JSON.parse(originalRecord);
        } catch (e) {
          console.error("Error parsing originalRecordJson:", e);
          originalRecord = {};
        }
      }

      if (typeof modifiedRecord === "string") {
        try {
          modifiedRecord = JSON.parse(modifiedRecord);
        } catch (e) {
          console.error("Error parsing modifiedRecordJson:", e);
          modifiedRecord = {};
        }
      }

      return {
        ...item,
        originalRecord,
        modifiedRecord,
        formattedModifiedAt: formatDate(item.modifiedAt),
        formattedHandledAt: formatDate(item.handledAt),
      };
    });

    return processedData.filter((change) => {
      const matchStatus =
        filters.status === "all" || change.status === filters.status;

      const modifiedDate = change.modifiedAt
        ? new Date(change.modifiedAt)
        : null;

      const matchDateFrom =
        !filters.dateFrom || !modifiedDate
          ? true
          : modifiedDate >= new Date(filters.dateFrom);

      const matchDateTo =
        !filters.dateTo || !modifiedDate
          ? true
          : modifiedDate <= new Date(filters.dateTo);

      // Filtro per codice AFE
      const matchCode =
        !filters.code ||
        (change.afeCode &&
          String(change.afeCode)
            .toLowerCase()
            .includes(filters.code.toLowerCase()));

      return matchStatus && matchDateFrom && matchDateTo && matchCode;
    });
  };

  // Cerca le differenze tra i record
  const findDifferences = (originalRecord, modifiedRecord) => {
    if (!originalRecord || !modifiedRecord) return [];

    const differences = [];
    const allKeys = new Set([
      ...Object.keys(originalRecord),
      ...Object.keys(modifiedRecord),
    ]);

    allKeys.forEach((key) => {
      // Ignora campi interni o di sistema
      if (
        key === "id" ||
        key === "hasChanges" ||
        key === "hasPendingChanges" ||
        key === "lastModifiedAt" ||
        key === "lastModifiedBy" ||
        key === "recentlyApproved" ||
        key === "dataSourceLastUpdate"
      ) {
        return;
      }

      const originalValue = originalRecord[key];
      const modifiedValue = modifiedRecord[key];

      // Considera solo campi che sono stati modificati
      if (JSON.stringify(originalValue) !== JSON.stringify(modifiedValue)) {
        differences.push({
          field: key,
          oldValue: originalValue,
          newValue: modifiedValue,
        });
      }
    });

    return differences;
  };

  // Formatta il valore per la visualizzazione
  const formatValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  // Ottiene il nome visualizzato del campo
  const getFieldDisplayName = (field) => {
    const fieldNameMap = {
      afeCode: "AFE Code",
      afeDesc: "Description",
      afeFyscalYear: "Fiscal Year",
      fiscalYearClosure: "Closure Year",
      fiscalYearRecord: "Fiscal Year Record",
      responsible: "Responsible",
      programId: "Program ID",
      userResponsible: "User Responsible",
      companyCode: "Company Code",
      ou: "OU",
      localCorporate: "Local/Corporate",
      catAfe: "Category",
      totalAfe: "Total AFE",
      capexYear: "CAPEX Year",
      spesoProgressivo: "Speso Progressivo",
      raggruppamento1: "Raggruppamento 1",
      raggruppamento2: "Raggruppamento 2",
      raggruppamento3: "Raggruppamento 3",
      raggruppamento1_SAP: "Raggruppamento 1 SAP",
      raggruppamento2_SAP: "Raggruppamento 2 SAP",
      raggruppamento3_SAP: "Raggruppamento 3 SAP",
    };

    return fieldNameMap[field] || field;
  };

  useEffect(() => {
    if (open && history && Array.isArray(history)) {
      // Process the data
      const filtered = applyFilters(history);
      setFilteredData(filtered);

      if (historyTableRef.current) {
        if (tableInstance.current) {
          tableInstance.current.destroy();
        }

        // Setup columns for record-level changes
        const columns = [
          { title: "ID", field: "id", width: 60 },
          { title: "AFE Code", field: "afeCode", width: 100, tooltip: true },
          {
            title: "Modificato da",
            field: "modifiedBy",
            width: 150,
            tooltip: true,
          },
          {
            title: "Data Modifica",
            field: "formattedModifiedAt",
            sorter: function (a, b, aRow, bRow) {
              const dateA = aRow.getData().modifiedAt
                ? new Date(aRow.getData().modifiedAt)
                : null;
              const dateB = bRow.getData().modifiedAt
                ? new Date(bRow.getData().modifiedAt)
                : null;
              if (!dateA && !dateB) return 0;
              if (!dateA) return -1;
              if (!dateB) return 1;
              return dateA - dateB;
            },
            width: 150,
          },
          {
            title: "Stato",
            field: "status",
            formatter: (cell) => {
              const value = cell.getValue();
              const styles = {
                approved: {
                  class: "bg-green-100 text-green-800",
                  text: "Approvato",
                },
                rejected: {
                  class: "bg-red-100 text-red-800",
                  text: "Rifiutato",
                },
                pending: {
                  class: "bg-yellow-100 text-yellow-800",
                  text: "In attesa",
                },
                default: { class: "bg-gray-100 text-gray-800", text: value },
              };
              const style = styles[value] || styles.default;
              return `<span class="px-2 py-1 rounded ${style.class}">${style.text}</span>`;
            },
            width: 100,
          },
          {
            title: "Gestito da",
            field: "handledBy",
            width: 150,
            tooltip: true,
          },
          {
            title: "Data Gestione",
            field: "formattedHandledAt",
            sorter: function (a, b, aRow, bRow) {
              const dateA = aRow.getData().handledAt
                ? new Date(aRow.getData().handledAt)
                : null;
              const dateB = bRow.getData().handledAt
                ? new Date(bRow.getData().handledAt)
                : null;
              if (!dateA && !dateB) return 0;
              if (!dateA) return -1;
              if (!dateB) return 1;
              return dateA - dateB;
            },
            width: 150,
          },
          {
            title: "Note",
            field: "notes",
            width: 200,
            tooltip: true,
          },
          {
            title: "Dettagli",
            width: 80,
            formatter: function (cell) {
              const id = cell.getRow().getData().id;
              // Pulsante con stile Material UI e icona di espansione
              return `<button 
                        class="details-btn MuiButtonBase-root MuiButton-root MuiButton-outlined MuiButton-outlinedPrimary MuiButton-sizeSmall"
                        data-id="${id}"
                        style="
                          font-family: 'Roboto', sans-serif;
                          font-size: 0.8125rem;
                          min-width: 64px;
                          padding: 3px 9px;
                          border-radius: 4px;
                          border: 1px solid rgba(25, 118, 210, 0.5);
                          background-color: transparent;
                          color: #1976d2;
                          cursor: pointer;
                          text-transform: none;
                          font-weight: 500;
                          transition: border 0.3s, background-color 0.3s;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          gap: 4px;
                        "
                        onmouseover="this.style.backgroundColor='rgba(25, 118, 210, 0.04)'; this.style.borderColor='#1976d2';"
                        onmouseout="this.style.backgroundColor='transparent'; this.style.borderColor='rgba(25, 118, 210, 0.5)';"
                      >
                        <span>Info</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>`;
            },
            cellClick: function (e, cell) {
              // Intercetta anche i click sugli elementi interni del pulsante
              let target = e.target;
              while (target && !target.classList.contains("details-btn")) {
                target = target.parentNode;
                if (target.tagName === "BUTTON") break;
              }

              if (target && target.classList.contains("details-btn")) {
                const id = parseInt(target.getAttribute("data-id"));
                toggleRow(id);
              }
            },
            hozAlign: "center",
          },
        ];

        tableInstance.current = new Tabulator(historyTableRef.current, {
          data: filtered,
          columns: columns,
          layout: "fitColumns",
          pagination: "local",
          paginationSize: 15,
          movableColumns: true,
          resizableColumns: true,
          initialSort: [{ column: "formattedModifiedAt", dir: "desc" }],
        });
      }
    }

    return () => {
      if (tableInstance.current) {
        tableInstance.current.destroy();
      }
    };
  }, [history, filters, open]);

  const handleFilterChange = (key) => (e) => {
    onFiltersChange?.({
      ...filters,
      [key]: e.target.value,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Storico Modifiche</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            mb: 4,
            p: 2,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Filtri
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Stato"
                value={filters.status}
                onChange={handleFilterChange("status")}
                size="small"
                fullWidth
              >
                <MenuItem value="all">Tutti</MenuItem>
                <MenuItem value="approved">Approvati</MenuItem>
                <MenuItem value="rejected">Rifiutati</MenuItem>
                <MenuItem value="pending">In attesa</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="Da data"
                value={filters.dateFrom}
                onChange={handleFilterChange("dateFrom")}
                size="small"
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="A data"
                value={filters.dateTo}
                onChange={handleFilterChange("dateTo")}
                size="small"
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="AFE Code"
                value={filters.code}
                onChange={handleFilterChange("code")}
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>

        {/* Tabella principale */}
        <div
          ref={historyTableRef}
          style={{ height: "400px", marginBottom: "20px" }}
        ></div>

        {/* Dettaglio delle modifiche per record espansi */}
        {filteredData.map((item) => (
          <Collapse
            key={item.id}
            in={expandedRows[item.id] || false}
            timeout="auto"
            unmountOnExit
            sx={{
              mb: 2,
              mt: 1,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Box sx={{ p: 2, bgcolor: "background.paper" }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Dettagli modifiche per AFE {item.afeCode}
              </Typography>

              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Campo</TableCell>
                      <TableCell>Valore Originale</TableCell>
                      <TableCell>Valore Modificato</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {findDifferences(
                      item.originalRecord,
                      item.modifiedRecord
                    ).map((diff, index) => (
                      <TableRow
                        key={index}
                        sx={{ bgcolor: "rgba(255, 243, 224, 0.3)" }}
                      >
                        <TableCell>{getFieldDisplayName(diff.field)}</TableCell>
                        <TableCell>{formatValue(diff.oldValue)}</TableCell>
                        <TableCell
                          sx={{ fontWeight: "bold", color: "primary.main" }}
                        >
                          {formatValue(diff.newValue)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {findDifferences(item.originalRecord, item.modifiedRecord)
                      .length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          Nessuna differenza trovata o dati incompleti
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        ))}
      </DialogContent>
      <DialogActions>
        <Q8Button
          variant="secondary"
          label="Chiudi"
          onClick={onClose}
          size="small"
        />
      </DialogActions>
    </Dialog>
  );
};

ChangeHistoryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  history: PropTypes.array,
  filters: PropTypes.shape({
    status: PropTypes.string,
    dateFrom: PropTypes.string,
    dateTo: PropTypes.string,
    code: PropTypes.string,
  }),
  onFiltersChange: PropTypes.func,
};

ChangeHistoryDialog.defaultProps = {
  history: [],
  filters: {
    status: "all",
    dateFrom: "",
    dateTo: "",
    code: "",
  },
  onFiltersChange: () => {},
};

export default ChangeHistoryDialog;
